import { useEffect, useState } from 'react'
import { Alert, Button } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '~/state/useStore'
import { useLatestRevision } from '~/api/hooks/useCatalog'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

// Renders the "data freshened upstream — click to refetch" banner.
// State machine:
//   1. On mount, latest.data is undefined (TanStack pending). renderedRev=null;
//      isStale(null) = false, no banner.
//   2. First non-null latest.data captures into renderedRev (the snapshot of
//      "what the user is currently looking at") + writes through to the store.
//   3. Polling fires every 60s; if a newer rev arrives, store.latestRevision
//      updates but renderedRev stays at the captured value -> isStale returns
//      true -> banner appears.
//   4. User clicks Refresh -> all walk_* queries invalidate (re-fetch
//      with current filters) + renderedRev snaps to the new latest, clearing
//      the banner.
export function StalenessIndicator() {
  const latest = useLatestRevision()
  const setRevision = useStore((s) => s.system.setRevision)
  // Subscribe to the value, not the isStale function ref — the function ref
  // never changes, so zustand wouldn't trigger a re-render on revision updates.
  const latestRevision = useStore((s) => s.system.latestRevision)
  const qc = useQueryClient()
  const [renderedRev, setRenderedRev] = useState<number | null>(null)

  useEffect(() => {
    if (typeof latest.data === 'number') setRevision(latest.data)
  }, [latest.data, setRevision])

  useEffect(() => {
    if (typeof latest.data === 'number' && renderedRev === null) {
      setRenderedRev(latest.data)
    }
  }, [latest.data, renderedRev])

  const isStale = latestRevision !== null && renderedRev !== null && latestRevision > renderedRev
  if (!isStale) return null

  return (
    <Alert
      severity="info"
      action={
        <Button
          onClick={() => {
            qc.invalidateQueries({
              predicate: (q) =>
                Array.isArray(q.queryKey) &&
                typeof q.queryKey[0] === 'string' &&
                q.queryKey[0].startsWith('walk_'),
            })
            if (typeof latest.data === 'number') setRenderedRev(latest.data)
          }}
        >
          {DIALOGS.refresh}
        </Button>
      }
    >
      {DIALOGS.dataUpdated}
    </Alert>
  )
}
